// ═══════════════════════════════════════════════════════════════════════════
// PAGE PROGRAMS — fragments partagés page d'édition ↔ modale
// Extrait de page-programs.ts (découpe + DÉDUP 2026-07) : `renderProjCheckboxes`
// était dupliqué À L'IDENTIQUE entre renderProgramEditPage et showProgramModal
// (à un seul détail près : sélection via Set.has vs Array.includes, ici
// abstraite en prédicat `isSelected`). Audit dette 2026-09-13 (D4b) : même
// sort pour les champs du bloc Général, la lecture du formulaire et le câblage
// des saisies. Ce qui est aussi commun au PROJET (puces, comités, onglets) vit
// dans `pilots-form`. Les builders sont PURS — l'état de sélection mutable
// reste dans chaque appelant, lu au moment du rendu.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t, toast.

import type { ProjectMin } from './page-programs.js';
import { readCommitteePilots, wireCodeUppercase, type CommitteePilot } from './pilots-form.js';

/** Cases à cocher des projets d'un programme (filtrées par recherche). `isSelected`
 * abstrait la source de sélection (Set côté page d'édition, tableau côté modale). */
export function buildProgramProjCheckboxes(
  projects: ReadonlyArray<ProjectMin>,
  filter: string,
  isSelected: (id: number) => boolean,
): string {
  const filtered = projects.filter((pr) => {
    if (!filter) return true;
    return pr.name.toLowerCase().includes(filter) || (pr.code || '').toLowerCase().includes(filter);
  });
  if (!filtered.length)
    return `<span style="color:var(--text3);font-size:12px">${esc(t('pagePrograms.noResult'))}</span>`;
  return filtered
    .map(
      (
        pr,
      ) => `<label style="display:flex;align-items:center;gap:6px;font-size:12px;margin-bottom:3px;cursor:pointer">
      <input type="checkbox" class="prog-proj-chk" value="${pr.id}" ${isSelected(pr.id) ? 'checked' : ''}>
      <span style="font-family:var(--font-mono);font-size:11px;color:var(--text3);min-width:40px">${esc(pr.code || '')}</span>
      ${esc(pr.name)}
    </label>`,
    )
    .join('');
}

export interface ProgramFields {
  code: string;
  name: string;
  date_start?: string | null;
  date_end?: string | null;
}

/** Champs du bloc Général : code, nom, puis dates de début et de fin. */
export function buildProgramGeneralFields(p: ProgramFields): string {
  return `
    <div class="form-row">
      <div class="form-group" style="flex:0 0 90px"><label>${esc(t('pagePrograms.lblCode'))}</label>
        <input type="text" id="pm-code" maxlength="5" value="${esc(p.code)}"
          placeholder="PG04" style="text-transform:uppercase;font-family:var(--font-mono);width:100%"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('pagePrograms.lblName'))}</label>
        <input type="text" id="pm-name" value="${esc(p.name)}" placeholder="${esc(t('pagePrograms.phName'))}" style="width:100%"></div>
    </div>
    <div class="form-row mt-2">
      <div class="form-group" style="flex:1"><label>${esc(t('pagePrograms.lblStart'))}</label>
        <input type="date" id="pm-start" value="${esc(p.date_start || '')}" style="width:100%"></div>
      <div class="form-group" style="flex:1"><label>${esc(t('pagePrograms.lblEnd'))}</label>
        <input type="date" id="pm-end" value="${esc(p.date_end || '')}" style="width:100%"></div>
    </div>`;
}

export interface ProgramPayload {
  code: string;
  name: string;
  date_start: string | null;
  date_end: string | null;
  project_ids: number[];
  pilots: number[];
  committee_pilots: CommitteePilot[];
}

/** Lit le formulaire programme. Code ou nom vide : toast d'erreur et `null`. */
export function readProgramPayload(selectedPilots: Set<number>): ProgramPayload | null {
  const codeEl = document.getElementById('pm-code') as HTMLInputElement | null;
  const nameEl = document.getElementById('pm-name') as HTMLInputElement | null;
  const code = (codeEl?.value || '').trim().toUpperCase();
  const name = (nameEl?.value || '').trim();
  if (!code) {
    toast(t('pagePrograms.errCodeRequired'), 'error');
    return null;
  }
  if (!name) {
    toast(t('pagePrograms.errNameRequired'), 'error');
    return null;
  }
  const project_ids = Array.from(
    document.querySelectorAll<HTMLInputElement>('.prog-proj-chk:checked'),
  ).map((c) => parseInt(c.value));
  const startEl = document.getElementById('pm-start') as HTMLInputElement | null;
  const endEl = document.getElementById('pm-end') as HTMLInputElement | null;
  return {
    code,
    name,
    date_start: startEl?.value || null,
    date_end: endEl?.value || null,
    project_ids,
    pilots: Array.from(selectedPilots),
    committee_pilots: readCommitteePilots('pgm'),
  };
}

/** Code en majuscules, et filtrage de la liste des projets à la saisie. */
export function wireProgramFormInputs(renderProjCheckboxes: (filter: string) => string): void {
  wireCodeUppercase();
  document.getElementById('pm-proj-search')?.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    const list = document.getElementById('pm-proj-list');
    if (list) list.innerHTML = renderProjCheckboxes((target?.value || '').toLowerCase().trim());
  });
}
